import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import ClBackdrop from '../ClBackdrop';
import { TransitionProps } from '../types';
import { applyClasses } from '../../lib';
import { CreateElement, VNodeData } from 'vue';
import { Portal } from 'portal-vue';

type Classes = {
  root: string | object;
  backdrop: string | object;
  slot: string | object;
};

@Component({
  components: {
    ClBackdrop,
    Portal
  }
})
export default class ClModal extends Vue {
  $cluiDisablePortal?: boolean;

  @Prop({
    type: Object as () => TransitionProps
  })
  public backdropTransitionProps!: TransitionProps;

  @Prop({
    type: String
  })
  public backdropTransitionDuration!: string;

  /**
   * the classnames of the internal components
   */
  @Prop({
    type: Object as () => Classes,
    default: () => ({
      root: '',
      slot: '',
      backdrop: ''
    })
  })
  public classes!: Classes;

  @Prop({
    type: Object,
    default: () => ({})
  })
  public clBackdropProps!: object;

  @Prop({
    type: Boolean,
    default: false
  })
  public disableBackdropClick!: boolean;

  /**
   * If `true`, the dialog will be rendered apart from the portal.
   * This is useful when your current component doesnt have a cl-provider.
   */
  @Prop({
    type: Boolean,
    default: false
  })
  public disablePortal!: boolean;

  @Prop({
    type: Boolean,
    default: false
  })
  public hideBackdrop!: boolean;

  @Prop({
    type: Boolean,
    default: false
  })
  public open!: boolean;

  @Prop({
    type: Object,
    default: () => ({})
  })
  public rootProps!: object;

  @Prop({
    type: Object as () => TransitionProps
  })
  public slotTransitionProps!: TransitionProps;

  @Prop({
    type: String,
    default: ''
  })
  public slotTransitionDuration!: string;

  public handleBackdropClick(event: Event) {
    this.$emit('backdrop-click', event);

    if (!this.disableBackdropClick) {
      this.$emit('close', event);
    }
  }

  get computedRootClass(): object {
    return applyClasses(
      {
        'cl-modal': true,
        'cl-modal--open': this.open
      },
      (this.rootProps as any).class,
      this.classes.root
    );
  }

  get computedSlotClass(): object {
    return applyClasses('cl-modal__slot', this.classes.slot);
  }

  get computedDisablePortal(): boolean {
    return this.disablePortal === true || Boolean(this.$cluiDisablePortal);
  }

  // Note: this was intended to stop scrolling when a menu is open,
  // but it seems to be causing more harm than good, so I'm leaving it out.
  // @Watch('open')
  // public handleOpenChange(open: boolean): void {
  //   document.body.style.overflow = open ? 'hidden' : '';
  // }

  handleEntering(el: Element, done: () => void) {
    this.$emit('entering', el);
    done();
  }

  handleExiting(el: Element, done: () => void) {
    this.$emit('exiting', el);
    done();
  }

  render(h: CreateElement) {
    const modal = (
      <div class={this.computedRootClass} {...this.rootProps}>
        <transition {...this.backdropTransitionProps}>
          <ClBackdrop
            {...{
              props: this.clBackdropProps,
              on: {
                click: this.handleBackdropClick
              },
              class: this.classes.backdrop
            }}
            v-show={this.open && !this.hideBackdrop}
          />
        </transition>
        <transition
          {...this.slotTransitionProps}
          {...{
            on: {
              beforeEnter: (e: any) => this.$emit('enter', e),
              enter: this.handleEntering,
              afterEnter: (e: any) => this.$emit('entered', e),
              // beforeLeave: (e: any) => this.$emit('exit', e),
              // leave: this.handleExiting,
              afterLeave: (e: any) => this.$emit('exited', e)
            }
          }}
        >
          {this.open && (
            <div class={this.computedSlotClass}>{this.$slots.default}</div>
          )}
        </transition>
      </div>
    );
    return this.computedDisablePortal ? (
      modal
    ) : (
      <portal to="cl-provider">{modal}</portal>
    );
  }
}
